import {
  DeviceGroupDeviceModel,
  DeviceGroupModel
} from '@models/digital-signage/device-groups.model.ts';
import {
  PlaylistModel,
  PlaylistScreenOption
} from '@modules/digital-signage/model/playlists.model.ts';
import { DigitalSignageScreensModel } from '@modules/digital-signage/model/screens.model.ts';
import { DevicesRowDataProps } from '@modules/digital-signage/model/devices.model.ts';
import { ScreenMediaModel } from '@modules/digital-signage/model/screen-media-preview.model.ts';
import { TableState } from '@store/table-store/table.model.ts';

export enum ScreenOrientationEnum {
  Portrait = 'portrait',
  Landscape = 'landscape'
}

export enum AssignedEntityEnum {
  Screen = 'screen',
  Playlist = 'playlist'
}

export enum DeviceStatusFilterEnum {
  All = 'all',
  Active = 'active',
  Inactive = 'inactive'
}

export interface DefaultContentOption extends PlaylistScreenOption {
  type: AssignedEntityEnum;
  isCreate?: boolean;
}

export interface DefaultContentSelectProps {
  value: DefaultContentOption | null;
  options: DefaultContentOption[];
  loading: boolean;
  onChange: (option: DefaultContentOption | null) => void;
  onCreate: (title: string, type: AssignedEntityEnum) => void;
}

export interface ContentTypeChipProps {
  type: AssignedEntityEnum;
}

export interface GroupSettingsFormValues {
  assignedScreen: PlaylistScreenOption | null;
  assignedPlaylist: PlaylistScreenOption | null;
  groupName: string;
  devices: PlaylistScreenOption[];
}

export interface GroupSettingsProps {
  /** null when the device has no group yet; Save then creates one */
  group: DeviceGroupModel | null;
  deviceId?: string;
  deviceName?: string;
  tableState?: TableState;
  onClose: () => void;
  onSaved?: () => void;
}

export interface GroupSettingsHeaderProps {
  group?: DeviceGroupModel | null;
  deviceName?: string;
}

export interface GroupScreenPreviewProps {
  screen: ScreenMediaModel | null;
}

export enum StatusLozengeToneEnum {
  Success = 'success',
  Brand = 'brand',
  BrandSoft = 'brand_soft',
  Warning = 'warning',
  Neutral = 'neutral',
  Danger = 'danger',
  Info = 'info'
}

export interface StatusLozengeProps {
  tone: StatusLozengeToneEnum;
  label: string;
  className?: string;
}

export interface GroupDevicesProps {
  isDefaultGroup: boolean;
  addedDevicesCount: number;
  onReloadGroup?: () => void;
  onDeleteGroup?: () => void;
}

export interface GroupDetailsFieldsProps {
  isDefaultGroup: boolean;
  hasGroupName: boolean;
  addedDevicesCount: number;
}

export interface GroupNameNoticeProps {
  isDefaultGroup: boolean;
  hasGroupName: boolean;
  addedDevicesCount: number;
}

export interface MoveDevicesDialogProps {
  open: boolean;
  sourceDevices: DevicesRowDataProps[];
  isLoading: boolean;
  onClose: () => void;
  onSave: () => void;
}

export interface GroupDevicesListProps {
  selectedDevices: PlaylistScreenOption[];
  devicesById: Map<string, DeviceGroupDeviceModel>;
  devicesList: DeviceGroupDeviceModel[];
  disabled: boolean;
  isLoading: boolean;
  isAddDisabled: boolean;
  onDevicesChange: (devices: PlaylistScreenOption[]) => void;
}

export interface AddDevicesMenuProps {
  devices: DeviceGroupDeviceModel[];
  selectedDevices: PlaylistScreenOption[];
  disabled: boolean;
  onDevicesChange: (devices: PlaylistScreenOption[]) => void;
}

export interface DeviceGroupChipProps {
  name: string;
}

export interface DevicesListToolbarProps {
  query: string;
  onQueryChange: (query: string) => void;
  statusFilter: DeviceStatusFilterEnum;
  onStatusFilterChange: (filter: DeviceStatusFilterEnum) => void;
  statusCounts: Record<DeviceStatusFilterEnum, number>;
  disabled: boolean;
}

export interface GroupDeviceRowProps {
  option: PlaylistScreenOption;
  device?: DeviceGroupDeviceModel;
  checked: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}

export interface GroupActionsProps {
  onReload: () => void;
  onDelete: () => void;
}

export interface PreviewScreenParams {
  screensList: DigitalSignageScreensModel[];
  playlistsList: PlaylistModel[];
  screen: PlaylistScreenOption | null;
  playlist: PlaylistScreenOption | null;
  lastSelected: AssignedEntityEnum | null;
}

export interface UseGroupSettingsFormParams {
  group: DeviceGroupModel | null;
  deviceId?: string;
  deviceName?: string;
}

export interface UseGroupSettingsCloseParams {
  isDirty: boolean;
  hasOpenDialog: boolean;
  onClose: () => void;
}

export interface GroupSettingsFooterProps {
  hasChanges: boolean;
  isLoading: boolean;
  isCreatingFromDefault: boolean;
}
